import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { FileCheck, Download, Car, User, Euro, CheckCircle, XCircle, FileText, Loader2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Esiti disponibili per Admin in Gestione Pratiche
const ESITI_PRATICHE = [
  { value: 'approvato', label: 'APPROVATO', icon: CheckCircle, color: 'text-emerald-600' },
  { value: 'rifiutato', label: 'RIFIUTATO', icon: XCircle, color: 'text-red-600' }
];

const GestionePratichePage = () => {
  const { getAuthHeader } = useAuth();
  const [pratiche, setPratiche] = useState([]);
  const [loading, setLoading] = useState(true);
  const [esitoDialog, setEsitoDialog] = useState({ open: false, pratica: null });
  const [selectedEsito, setSelectedEsito] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchPratiche = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pratiche`, { headers: getAuthHeader() });
      setPratiche(response.data);
    } catch (error) {
      console.error('Error fetching pratiche:', error);
      toast.error('Errore nel caricamento pratiche');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchPratiche();
  }, [fetchPratiche]);

  const handleOpenEsitoDialog = (pratica) => {
    setSelectedEsito('');
    setNote('');
    setEsitoDialog({ open: true, pratica });
  };

  const handleSaveEsito = async () => {
    if (!selectedEsito || !esitoDialog.pratica) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/offers/${esitoDialog.pratica.id}/esito`, {
        esito: selectedEsito,
        notes: note || null
      }, { headers: getAuthHeader() });
      
      toast.success(selectedEsito === 'approvato' ? 'Pratica approvata!' : 'Pratica rifiutata');
      setEsitoDialog({ open: false, pratica: null });
      fetchPratiche();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async (offerId) => {
    try {
      const response = await axios.get(`${API}/offers/${offerId}/pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `offerta_${offerId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Errore nel download del PDF');
    }
  };

  const getEsitoLabel = (esito) => {
    const labels = {
      'richiesta_documentazione': 'Richiesta Documentazione',
      'pratica_credito': 'Pratica di Credito'
    };
    return labels[esito] || esito;
  };

  const getEsitoBadgeClass = (esito) => {
    if (esito === 'richiesta_documentazione') return 'bg-amber-100 text-amber-700';
    if (esito === 'pratica_credito') return 'bg-blue-100 text-blue-700';
    return 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gestione-pratiche-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Gestione Pratiche
          </h1>
          <p className="text-slate-500 mt-1">
            Valuta le pratiche in attesa di approvazione creditizia
          </p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          <FileCheck className="w-5 h-5 mr-2" />
          {pratiche.length} pratiche in attesa
        </Badge>
      </div>

      {/* Pratiche Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Pratiche da Valutare
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pratiche.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileCheck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Nessuna pratica in attesa di valutazione</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Agente</TableHead>
                    <TableHead>Veicolo</TableHead>
                    <TableHead>Canone</TableHead>
                    <TableHead>Stato Pratica</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pratiche.map((pratica) => (
                    <TableRow key={pratica.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className="font-medium">{pratica.contact_info?.nome_cognome || 'N/A'}</p>
                            <p className="text-xs text-slate-500">{pratica.contact_info?.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">{pratica.user_name}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Car className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className="font-medium text-sm">
                              {pratica.vehicle_info?.marca} {pratica.vehicle_info?.modello}
                            </p>
                            <p className="text-xs text-slate-500 truncate max-w-[200px]">
                              {pratica.vehicle_info?.versione}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Euro className="w-4 h-4 text-emerald-500" />
                          <span className="font-semibold text-emerald-600">
                            {pratica.canone_finale?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                          </span>
                          <span className="text-xs text-slate-500">/mese</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getEsitoBadgeClass(pratica.esito)}>
                          {getEsitoLabel(pratica.esito)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500">
                          {new Date(pratica.created_at).toLocaleDateString('it-IT')}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDownloadPdf(pratica.id)}
                            title="Scarica PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm"
                            className="bg-primary hover:bg-primary/90"
                            onClick={() => handleOpenEsitoDialog(pratica)}
                            data-testid={`valuta-${pratica.id}`}
                          >
                            <FileCheck className="w-4 h-4 mr-1" />
                            Valuta
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Esito Dialog */}
      <Dialog open={esitoDialog.open} onOpenChange={(open) => setEsitoDialog({ open, pratica: esitoDialog.pratica })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Valutazione Pratica
            </DialogTitle>
          </DialogHeader>
          {esitoDialog.pratica && (
            <div className="space-y-4 py-4">
              {/* Info Pratica */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="font-medium">{esitoDialog.pratica.contact_info?.nome_cognome}</span>
                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">PROSPECT</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Car className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600">
                    {esitoDialog.pratica.vehicle_info?.marca} {esitoDialog.pratica.vehicle_info?.modello}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Euro className="w-4 h-4 text-emerald-500" />
                  <span className="font-semibold text-emerald-600">
                    €{esitoDialog.pratica.canone_finale?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}/mese
                  </span>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Badge className={getEsitoBadgeClass(esitoDialog.pratica.esito)}>
                    {getEsitoLabel(esitoDialog.pratica.esito)}
                  </Badge>
                  <span className="text-xs text-slate-500">• Agente: {esitoDialog.pratica.user_name}</span>
                </div>
              </div>

              {/* Selezione Esito */}
              <div className="space-y-2">
                <Label>Esito Valutazione</Label>
                <Select value={selectedEsito} onValueChange={setSelectedEsito}>
                  <SelectTrigger data-testid="esito-pratica-select">
                    <SelectValue placeholder="Seleziona esito..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ESITI_PRATICHE.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <opt.icon className={`w-4 h-4 ${opt.color}`} />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Alert per Rifiuto */}
              {selectedEsito === 'rifiutato' && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700">
                      <p className="font-medium">Attenzione</p>
                      <p>La pratica verrà chiusa e conteggiata come "Rifiutata".</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Note */}
              <div className="space-y-2">
                <Label>Note (opzionale)</Label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Inserisci eventuali note sulla valutazione..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setEsitoDialog({ open: false, pratica: null })}
            >
              Annulla
            </Button>
            <Button 
              className={`flex-1 ${selectedEsito === 'rifiutato' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
              onClick={handleSaveEsito}
              disabled={!selectedEsito || saving}
              data-testid="confirm-valutazione-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : selectedEsito === 'rifiutato' ? (
                <XCircle className="w-4 h-4 mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              {selectedEsito === 'rifiutato' ? 'Rifiuta Pratica' : 'Approva Pratica'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionePratichePage;
